/**
 * WebsiteModal
 *
 * Modal to preview a website with video, schedule call button, and view website button.
 * Simplified layout matching the graphics template modal.
 */
import React, { useRef, useEffect, useState } from 'react';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import { 
  X, 
  Play,
  Heart,
  ExternalLink,
  Phone
} from 'lucide-react';
import './TemplateModal.css';
import { robocentQuickfireUrl } from '../../../data/scheduling/schedulingLinks';

// Function to convert Firebase Storage gs:// URLs to HTTP URLs
const convertFirebaseStorageUrl = (gsUrl) => {
  if (!gsUrl) return gsUrl;
  
  if (gsUrl.startsWith('http://') || gsUrl.startsWith('https://')) {
    return gsUrl;
  }
  
  if (!gsUrl.startsWith('gs://')) {
    return gsUrl;
  }
  
  const match = gsUrl.match(/^gs:\/\/([^\/]+)\/(.+)$/);
  if (!match) {
    return gsUrl;
  }
  
  const [, bucket, path] = match;
  return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(path)}?alt=media`;
};

const WebsiteModal = ({ 
  website, 
  isOpen, 
  onClose, 
  isSaved = false,
  onSaveWebsite,
  isLoggedIn = false
}) => {
  const modalRef = useRef(null);
  const videoRef = useRef(null);
  const [videoError, setVideoError] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  
  const { addToast } = useToast?.() || { addToast: (msg) => console.log(msg) };

  // Auto-play video when modal opens (muted)
  useEffect(() => {
    if (isOpen && videoRef.current && website?.videoUrl) {
      setVideoLoading(true);
      videoRef.current.muted = true;
      videoRef.current.currentTime = 0;
      setVideoError(false);
    }
  }, [isOpen, website]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;
      
      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle clicks outside the modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const handleScheduleCall = async () => {
    try {
      // Record Calendly click (non-blocking)
      try {
        const { post } = await import('../../../api/core');
        post('/websites/calendly-click', {}).catch(() => {});
      } catch (_) {}
      // Open RoboCent quickfire link in new tab
      window.open(robocentQuickfireUrl, '_blank', 'noopener,noreferrer');
      addToast('Opening scheduling page...', 'success');
    } catch (error) {
      console.error('Schedule call failed:', error);
      addToast('Failed to open scheduling page. Please try again.', 'error');
    }
  };

  const handleViewWebsite = async () => {
    if (!website?.websiteUrl) return;

    try {
      // Track website usage
      if (website?.id) {
        const { useWebsite } = await import('../../../api/websites/websitesApi');
        useWebsite(website.id).catch(err => {
          console.error('Failed to record website usage:', err);
        });
      }

      // Open website link in new tab
      window.open(website.websiteUrl, '_blank', 'noopener,noreferrer');
      addToast('Opening website...', 'success');
    } catch (error) {
      console.error('View website failed:', error);
      addToast('Failed to open website. Please try again.', 'error');
    }
  };

  // Handle save/unsave website
  const handleSaveClick = () => {
    if (onSaveWebsite) {
      onSaveWebsite(website.id, !isSaved);
    }
  };

  if (!isOpen || !website) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 md:p-4 bg-black/60 backdrop-blur-sm">
      <div 
        className="animate-in fade-in zoom-in-95 duration-200 ease-out"
        style={{ animationFillMode: 'forwards' }}
      >
        <div 
          ref={modalRef}
          className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden"
        >
          {/* Close button - positioned in corner */}
          <button 
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-full bg-white/90 dark:bg-gray-800/90 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-20 shadow-md"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </button>

          {/* Save button - positioned in corner */}
          {isLoggedIn && onSaveWebsite && (
            <button
              onClick={handleSaveClick}
              className={`absolute top-3 left-3 p-2 rounded-full z-20 shadow-md ${
                isSaved 
                  ? 'bg-gradient-to-r from-purple-500 to-purple-600' 
                  : 'bg-white/90 dark:bg-gray-800/90 hover:bg-gray-100 dark:hover:bg-gray-700'
              } transition-colors`}
              aria-label={isSaved ? "Unsave website" : "Save website"}
            >
              <Heart 
                className={`w-4 h-4 ${isSaved ? 'text-white fill-white' : 'text-gray-600 dark:text-gray-300'}`} 
              />
            </button>
          )}

          {/* Main Content */}
          <div className="p-6 pt-14 md:p-8 md:pt-14">
            {/* Inline-flex container ensures buttons match video width */}
            <div className="inline-flex flex-col">
              {/* Video player */}
              {website.videoUrl && !videoError ? (
                <div className="relative">
                  {/* Video Skeleton - shown while loading */}
                  {videoLoading && (
                    <div 
                      className="bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"
                      style={{
                        width: 'min(80vw, 600px)',
                        aspectRatio: '16/9'
                      }}
                    />
                  )}
                  
                  <video 
                    ref={videoRef}
                    src={convertFirebaseStorageUrl(website.videoUrl)}
                    controls
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="rounded-lg shadow-md"
                    style={{ 
                      display: videoLoading ? 'none' : 'block',
                      maxWidth: 'min(80vw, 600px)',
                      maxHeight: '60vh',
                      width: 'auto',
                      height: 'auto'
                    }}
                    onError={() => {
                      setVideoError(true);
                      setVideoLoading(false);
                    }}
                    onLoadedData={() => setVideoLoading(false)}
                    onCanPlay={() => setVideoLoading(false)}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              ) : (
                <div 
                  className="bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center shadow-md"
                  style={{
                    width: 'min(80vw, 600px)',
                    aspectRatio: '16/9'
                  }}
                >
                  <div className="text-center">
                    <Play className="w-16 h-16 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">Video unavailable</p>
                  </div>
                </div>
              )}

              {/* Info callout */}
              <div className="mt-5 mb-4">
                <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 dark:border-purple-800 dark:bg-purple-900/20">
                  <p className="text-sm font-medium text-purple-900 dark:text-purple-200">
                    Want a website like this? Schedule a call with our team to discuss your project.
                  </p>
                </div>
              </div>

              {/* Schedule Call Button */}
              <button
                onClick={handleScheduleCall}
                className="w-full py-3.5 px-6 rounded-lg text-base font-semibold text-white transition-all flex items-center justify-center shadow-md hover:shadow-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              >
                <Phone className="w-5 h-5 mr-2" />
                Schedule a Call
              </button>

              {/* View Website Button */}
              {website.websiteUrl && (
                <button
                  onClick={handleViewWebsite}
                  className="w-full mt-3 py-3 px-6 rounded-lg text-base font-semibold text-purple-600 dark:text-purple-400 transition-all flex items-center justify-center border-2 border-purple-600 dark:border-purple-500 bg-white dark:bg-gray-800 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                >
                  <ExternalLink className="w-5 h-5 mr-2" />
                  View Website
                </button>
              )}

              {/* Close button - only visible on mobile */}
              <button
                onClick={onClose}
                className="w-full mt-3 py-2.5 px-3 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors lg:hidden"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebsiteModal;
